import { CtaDefinition } from './cta.models';

/** Delay before the page CTA list appears after the page is rendered or media closes. */
export const CTA_DISPLAY_DELAY_MS = 2000;

/** Milliseconds in one hour — converts a CTA's `cooldownHours` into an expiry. */
export const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * localStorage key prefix for per-user CTA cooldown state. The user identity is
 * appended as a non-reversible hash so cooldowns are scoped per account and no
 * email is stored in clear text.
 */
export const CTA_COOLDOWN_STORAGE_PREFIX = 'fb-cta-cooldown';

/** Portal page paths that support contextual CTAs. */
export const CTA_SUPPORTED_PAGES = {
  personalInfo: '/personal-info',
  calendarHolidays: '/calendar/holidays',
  calendarVacationPlanner: '/calendar/vacation-planner',
  educationCourses: '/education/courses',
  educationMyCourses: '/education/my-courses',
} as const;

/** Shown when all context CTAs are in cooldown, and always on `/personal-info`. */
export const FALLBACK_CTA: CtaDefinition = {
  id: 'anyQuestionsLeft',
  labelKey: 'cta.anyQuestionsLeft',
  prompt: null,
  order: 0,
  cooldownHours: 0,
  availableOnFullPage: true,
  isFallback: true,
  isStaticResponse: true,
  staticResponseKey: 'chatBot.greeting',
};

const CALENDAR_HOLIDAYS_CTAS: CtaDefinition[] = [
  {
    id: 'holidaysDuringVacation',
    labelKey: 'cta.holidaysDuringVacation',
    prompt: 'What happens if a vacation day overlaps with an official holiday in my location?',
    order: 1,
    cooldownHours: 168,
    availableOnFullPage: true,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
  {
    id: 'differentLocationRules',
    labelKey: 'cta.differentLocationRules',
    prompt: 'Which vacation and holiday rules are applied for my location?',
    order: 2,
    cooldownHours: 168,
    availableOnFullPage: false,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
  {
    id: 'weekendsInVacationBalance',
    labelKey: 'cta.weekendsInVacationBalance',
    prompt: 'Are weekends included into a vacation balance?',
    order: 3,
    cooldownHours: 168,
    availableOnFullPage: false,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
  {
    id: 'nextLocalHoliday',
    labelKey: 'cta.nextLocalHoliday',
    prompt: 'When is the next official holiday in my location?',
    order: 4,
    cooldownHours: 168,
    availableOnFullPage: true,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
];

const CALENDAR_VACATION_PLANNER_CTAS: CtaDefinition[] = [
  {
    id: 'vacationBalance',
    labelKey: 'cta.vacationBalance',
    prompt: 'How many days are left in my vacation balance?',
    order: 1,
    cooldownHours: 72,
    availableOnFullPage: true,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
  {
    id: 'sickLeaveDuringVacation',
    labelKey: 'cta.sickLeaveDuringVacation',
    prompt: 'What happens if I take sick leave during my vacation?',
    order: 2,
    cooldownHours: 168,
    availableOnFullPage: false,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
  {
    id: 'carryOverRules',
    labelKey: 'cta.carryOverRules',
    prompt: 'What are the carry-over rules for vacation?',
    order: 3,
    cooldownHours: 168,
    availableOnFullPage: false,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
  {
    id: 'overlappingVacationRules',
    labelKey: 'cta.overlappingVacationRules',
    prompt: 'What are the rules for overlapping vacations with my colleagues?',
    order: 4,
    cooldownHours: 168,
    availableOnFullPage: false,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
];

const EDUCATION_CTAS: CtaDefinition[] = [
  {
    id: 'externalTrainingConferences',
    labelKey: 'cta.externalTrainingConferences',
    prompt: 'Are external training and conferences available?',
    order: 1,
    cooldownHours: 72,
    availableOnFullPage: true,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
  {
    id: 'requestExternalTraining',
    labelKey: 'cta.requestExternalTraining',
    prompt: 'How can I request external training?',
    order: 2,
    cooldownHours: 72,
    availableOnFullPage: false,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
  {
    id: 'internalTrainingOptions',
    labelKey: 'cta.internalTrainingOptions',
    prompt: 'What internal training options are available?',
    order: 3,
    cooldownHours: 72,
    availableOnFullPage: false,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
  {
    id: 'becomeMentor',
    labelKey: 'cta.becomeMentor',
    prompt: 'Can I become a mentor?',
    order: 4,
    cooldownHours: 168,
    availableOnFullPage: false,
    isFallback: false,
    isStaticResponse: false,
    staticResponseKey: null,
  },
];

/**
 * CTA configuration per supported page.
 * `/personal-info` has no context CTAs, so only the always-on fallback is shown.
 */
export const PAGE_CTA_CONFIG: Readonly<Record<string, CtaDefinition[]>> = {
  [CTA_SUPPORTED_PAGES.personalInfo]: [],
  [CTA_SUPPORTED_PAGES.calendarHolidays]: CALENDAR_HOLIDAYS_CTAS,
  [CTA_SUPPORTED_PAGES.calendarVacationPlanner]: CALENDAR_VACATION_PLANNER_CTAS,
  [CTA_SUPPORTED_PAGES.educationCourses]: EDUCATION_CTAS,
  [CTA_SUPPORTED_PAGES.educationMyCourses]: EDUCATION_CTAS,
};

/** CTAs available in the full-page chatbot, in their configured display order. */
export const FULL_PAGE_CTAS: CtaDefinition[] = Object.values(PAGE_CTA_CONFIG)
  .flat()
  .filter(
    (cta, index, all) => cta.availableOnFullPage && all.findIndex(item => item.id === cta.id) === index,
  );
